import React, { useState, useEffect } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Swal from "sweetalert2";
import PrestamosParametrizadosService from "../../services/prestamosParametrizadosService";
import GruposAfinidadService from "../../services/gruposAfinidadService";
import ClientesSelector from "./ClientesSelector";
import {
  ALCANCES,
  DESCRIPCION_MAX,
  MODOS,
  TITULO_MAX,
  extraerLista,
  formatPesos,
  mostrarErrorApi,
  textoVisibilidad,
} from "./utils";

// ── Helpers de estilo (mismos que ReglaFormModal de adelantos) ────────────────
const inp =
  "w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-custom-green-ccard-1 transition-colors";
const lbl = "text-xs font-medium text-gray-500 mb-1 block";

const EMPTY_FORM = {
  titulo: "",
  descripcion: "",
  alcance: ALCANCES.GRUPO,
  id_grupo_afinidad: "",
  score_min: "",
  score_max: "",
  tipoMonto: "FIJO", // FIJO | DISPONIBLE
  monto: "",
  id_plan: "",
  cuotas: [],
  modo: MODOS.AMBOS,
  requiere_disponible: "S",
};

/** Bloque numerado con título y explicación. */
function Seccion({ numero, titulo, ayuda, children }) {
  return (
    <section className="space-y-2 border-t border-gray-100 pt-4 first:border-t-0 first:pt-0">
      <div>
        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-custom-green-ccard-1 text-[11px] text-white">
            {numero}
          </span>
          {titulo}
        </h3>
        {ayuda && <p className="mt-0.5 pl-7 text-xs text-gray-500">{ayuda}</p>}
      </div>
      <div className="pl-7 space-y-2">{children}</div>
    </section>
  );
}

/** Opción tipo "card" para los radios de configuración. */
function OpcionRadio({ name, value, checked, onChange, titulo, detalle, disabled }) {
  return (
    <label
      className={`flex gap-3 rounded-xl border px-3 py-2.5 transition-colors ${
        disabled
          ? "cursor-not-allowed border-gray-100 bg-gray-50 opacity-60"
          : checked
            ? "cursor-pointer border-custom-green-ccard-1 bg-green-50"
            : "cursor-pointer border-gray-200 hover:bg-gray-50"
      }`}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={() => onChange(value)}
        className="mt-1 accent-green-600"
      />
      <span>
        <span className="block text-sm font-medium text-gray-700">{titulo}</span>
        <span className="block text-xs text-gray-500">{detalle}</span>
      </span>
    </label>
  );
}

/**
 * Modal para crear y editar Préstamos Parametrizados.
 *
 * Props:
 *   prestamo — null → crear | objeto (de GET /prestamos-parametrizados) → editar
 *   onClose  — cierra sin guardar
 *   onSaved  — llamado tras POST/PUT exitoso
 */
export default function PrestamoParametrizadoFormModal({ prestamo, onClose, onSaved }) {
  const isEdit = prestamo !== null && prestamo !== undefined;

  const [form, setForm] = useState(EMPTY_FORM);
  const [clientes, setClientes] = useState([]);

  const [grupos, setGrupos] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [loadingDatos, setLoadingDatos] = useState(true);
  const [errorDatos, setErrorDatos] = useState(null);

  const [saving, setSaving] = useState(false);
  const [validationError, setValidationError] = useState(null);

  // ── Carga de grupos, planes y (al editar) la lista de clientes ──────────────
  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        const [listaGrupos, resPlanes, resDetalle] = await Promise.all([
          GruposAfinidadService.getAll(),
          PrestamosParametrizadosService.getPlanes(),
          isEdit && prestamo?.alcance === ALCANCES.CLIENTES
            ? PrestamosParametrizadosService.getById(prestamo.id)
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setGrupos(listaGrupos);
        setPlanes(extraerLista(resPlanes));
        const detalle = resDetalle?.data?.data;
        if (Array.isArray(detalle?.clientes)) setClientes(detalle.clientes);
      } catch {
        if (!cancelled)
          setErrorDatos("No se pudieron cargar los grupos de afinidad, los planes o los clientes.");
      } finally {
        if (!cancelled) setLoadingDatos(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [isEdit, prestamo]);

  // ── Pre-carga en modo editar ───────────────────────────────────────────────
  useEffect(() => {
    if (isEdit && prestamo) {
      setForm({
        titulo: prestamo.titulo ?? "",
        descripcion: prestamo.descripcion ?? "",
        alcance: prestamo.alcance ?? ALCANCES.GRUPO,
        id_grupo_afinidad: prestamo.idGrupoAfinidad ?? "",
        score_min: prestamo.scoreMin != null ? String(prestamo.scoreMin) : "",
        score_max: prestamo.scoreMax != null ? String(prestamo.scoreMax) : "",
        tipoMonto: prestamo.monto == null ? "DISPONIBLE" : "FIJO",
        monto: prestamo.monto != null ? String(prestamo.monto) : "",
        id_plan: prestamo.idPlan ?? prestamo.plan?.id ?? "",
        cuotas: Array.isArray(prestamo.cuotas) ? prestamo.cuotas.map(Number) : [],
        modo: prestamo.modo ?? MODOS.AMBOS,
        requiere_disponible: prestamo.requiereDisponible ?? "S",
      });
    } else {
      setForm(EMPTY_FORM);
      setClientes([]);
    }
  }, [prestamo, isEdit]);

  const set = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setValidationError(null);
  };

  // Sin monto fijo siempre requiere disponible
  const setTipoMonto = (tipo) => {
    setForm((f) => ({
      ...f,
      tipoMonto: tipo,
      requiere_disponible: tipo === "DISPONIBLE" ? "S" : f.requiere_disponible,
    }));
    setValidationError(null);
  };

  const planSeleccionado = planes.find((p) => String(p.id) === String(form.id_plan));
  const cuotasPlan = Array.isArray(planSeleccionado?.cuotas)
    ? planSeleccionado.cuotas.map(Number)
    : [];

  // Al cambiar de plan se conservan solo las cuotas que existen en el nuevo
  const handlePlan = (value) => {
    const nuevo = planes.find((p) => String(p.id) === String(value));
    const disponibles = Array.isArray(nuevo?.cuotas) ? nuevo.cuotas.map(Number) : [];
    setForm((f) => ({
      ...f,
      id_plan: value === "" ? "" : Number(value),
      cuotas: f.cuotas.filter((c) => disponibles.includes(c)),
    }));
    setValidationError(null);
  };

  const toggleCuota = (cuota) => {
    setForm((f) => ({
      ...f,
      cuotas: f.cuotas.includes(cuota)
        ? f.cuotas.filter((c) => c !== cuota)
        : [...f.cuotas, cuota].sort((a, b) => a - b),
    }));
    setValidationError(null);
  };

  const todasMarcadas =
    cuotasPlan.length > 0 && cuotasPlan.every((c) => form.cuotas.includes(c));

  const montoVariable = form.tipoMonto === "DISPONIBLE";
  const porClientes = form.alcance === ALCANCES.CLIENTES;
  const visibilidad = textoVisibilidad(form.modo, form.requiere_disponible, montoVariable);
  const nombreGrupo = grupos.find((g) => String(g.id) === String(form.id_grupo_afinidad))?.nombre;

  // ── Validación y envío ─────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    const titulo = form.titulo.trim();
    if (titulo.length < 3) {
      setValidationError("El título debe tener al menos 3 caracteres.");
      return;
    }

    let scoreMin = null;
    let scoreMax = null;
    if (porClientes) {
      if (clientes.length === 0) {
        setValidationError("Asigná al menos un cliente (de a uno o con un Excel).");
        return;
      }
    } else {
      if (!form.id_grupo_afinidad) {
        setValidationError("Debe seleccionar un Grupo de Afinidad.");
        return;
      }
      scoreMin = form.score_min === "" ? null : parseInt(form.score_min, 10);
      scoreMax = form.score_max === "" ? null : parseInt(form.score_max, 10);
      if ((scoreMin === null) !== (scoreMax === null)) {
        setValidationError(
          "El rango de score se carga completo (mínimo y máximo) o se deja vacío.",
        );
        return;
      }
      if (scoreMin !== null && scoreMin > scoreMax) {
        setValidationError("Score Mín no puede ser mayor que Score Máx.");
        return;
      }
    }

    let monto = null;
    if (!montoVariable) {
      monto = Number(form.monto);
      if (!form.monto || isNaN(monto) || monto <= 0) {
        setValidationError("Ingresá el monto del préstamo, o elegí \"Según el disponible del cliente\".");
        return;
      }
      monto = Math.round(monto * 100) / 100;
    }

    if (!form.id_plan) {
      setValidationError("Debe seleccionar un Plan de préstamo.");
      return;
    }
    if (form.cuotas.length === 0) {
      setValidationError("Tenés que habilitar al menos una cuota.");
      return;
    }

    const payload = {
      titulo,
      descripcion: form.descripcion,
      alcance: form.alcance,
      id_grupo_afinidad: porClientes ? null : Number(form.id_grupo_afinidad),
      score_min: scoreMin,
      score_max: scoreMax,
      clientes: porClientes ? clientes.map((c) => c.idCliente) : [],
      monto,
      id_plan: Number(form.id_plan),
      cuotas: form.cuotas,
      modo: form.modo,
      requiere_disponible: montoVariable ? "S" : form.requiere_disponible,
      activo: isEdit ? (prestamo.activo ? 1 : 0) : 1,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await PrestamosParametrizadosService.update(prestamo.id, payload);
      } else {
        await PrestamosParametrizadosService.create(payload);
      }
      Swal.fire({
        icon: "success",
        title: isEdit ? "Préstamo actualizado" : "Préstamo creado",
        position: "top-end",
        toast: true,
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
      onSaved();
    } catch (err) {
      // 409: conflicto de configuración con otros préstamos
      mostrarErrorApi(err, "No se pudo guardar el préstamo.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDisabled = loadingDatos || !!errorDatos || saving;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-800">
            {isEdit ? "Editar préstamo" : "Nuevo préstamo"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Cerrar modal"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5">
          {loadingDatos && (
            <div className="flex items-center justify-center py-8">
              <div className="h-7 w-7 rounded-full border-4 border-custom-green-ccard-1 border-t-transparent animate-spin" />
              <span className="ml-3 text-sm text-gray-500">Cargando datos…</span>
            </div>
          )}

          {!loadingDatos && errorDatos && (
            <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              {errorDatos}
            </div>
          )}

          {!loadingDatos && !errorDatos && (
            <form id="prestamo-form" onSubmit={handleSubmit} className="space-y-5">
              {/* 1. Texto */}
              <Seccion
                numero={1}
                titulo="Qué ve el socio"
                ayuda="El título y la descripción aparecen en la app, en la card del préstamo."
              >
                <div>
                  <label className={lbl}>Título *</label>
                  <input
                    className={inp}
                    value={form.titulo}
                    maxLength={TITULO_MAX}
                    onChange={(e) => set("titulo", e.target.value)}
                    placeholder="Ej: Préstamo Aniversario"
                  />
                  <p className="mt-1 text-right text-xs text-gray-400">
                    {form.titulo.length}/{TITULO_MAX}
                  </p>
                </div>
                <div>
                  <label className={lbl}>Descripción</label>
                  <textarea
                    className={`${inp} min-h-[100px] resize-y`}
                    value={form.descripcion}
                    maxLength={DESCRIPCION_MAX}
                    onChange={(e) => set("descripcion", e.target.value)}
                    placeholder={"Condiciones, beneficios, aclaraciones…\nCada Enter es un salto de línea en la app."}
                  />
                  <div className="mt-1 flex justify-between text-xs text-gray-400">
                    <span>Los saltos de línea se muestran igual en la app.</span>
                    <span>
                      {form.descripcion.length}/{DESCRIPCION_MAX}
                    </span>
                  </div>
                </div>
              </Seccion>

              {/* 2. Destinatarios */}
              <Seccion
                numero={2}
                titulo="¿A quién se le ofrece?"
                ayuda="Elegí una sola forma: por grupo de afinidad o a una lista de clientes puntuales."
              >
                <div className="grid gap-2 sm:grid-cols-2">
                  <OpcionRadio
                    name="alcance"
                    value={ALCANCES.GRUPO}
                    checked={form.alcance === ALCANCES.GRUPO}
                    onChange={(v) => set("alcance", v)}
                    titulo="Por grupo de afinidad"
                    detalle="A todos los socios del grupo; opcionalmente solo a un rango de score."
                  />
                  <OpcionRadio
                    name="alcance"
                    value={ALCANCES.CLIENTES}
                    checked={form.alcance === ALCANCES.CLIENTES}
                    onChange={(v) => set("alcance", v)}
                    titulo="A clientes puntuales"
                    detalle="Solo a los clientes que cargues, sin importar su grupo ni su score."
                  />
                </div>

                {!porClientes ? (
                  <>
                    <div>
                      <label className={lbl}>Grupo de Afinidad *</label>
                      <select
                        className={inp}
                        value={form.id_grupo_afinidad}
                        onChange={(e) =>
                          set(
                            "id_grupo_afinidad",
                            e.target.value === "" ? "" : Number(e.target.value),
                          )
                        }
                      >
                        <option value="">— Seleccionar —</option>
                        {grupos.map((g) => (
                          <option key={g.id} value={g.id}>
                            {g.nombre}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className={lbl}>Score Mín (opcional)</label>
                        <input
                          className={inp}
                          type="number"
                          min="0"
                          max="999"
                          value={form.score_min}
                          onChange={(e) => set("score_min", e.target.value)}
                          placeholder="0–999"
                        />
                      </div>
                      <div>
                        <label className={lbl}>Score Máx (opcional)</label>
                        <input
                          className={inp}
                          type="number"
                          min="0"
                          max="999"
                          value={form.score_max}
                          onChange={(e) => set("score_max", e.target.value)}
                          placeholder="0–999"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-400">
                      Score vacío = a todo el grupo, incluso a los socios sin score. Con rango, solo a
                      los que tengan un score dentro del rango.
                    </p>
                  </>
                ) : (
                  <ClientesSelector clientes={clientes} onChange={setClientes} />
                )}
              </Seccion>

              {/* 3. Monto */}
              <Seccion
                numero={3}
                titulo="Monto del préstamo"
                ayuda="Podés fijar un monto o dejar que el socio elija hasta lo que le permite su disponible."
              >
                <div className="grid gap-2 sm:grid-cols-2">
                  <OpcionRadio
                    name="tipoMonto"
                    value="FIJO"
                    checked={!montoVariable}
                    onChange={setTipoMonto}
                    titulo="Monto fijo"
                    detalle="El socio ve y saca exactamente este monto."
                  />
                  <OpcionRadio
                    name="tipoMonto"
                    value="DISPONIBLE"
                    checked={montoVariable}
                    onChange={setTipoMonto}
                    titulo="Según el disponible del cliente"
                    detalle="El socio elige cuánto pedir, entre el monto mínimo y su disponible para préstamos."
                  />
                </div>
                {!montoVariable ? (
                  <div>
                    <label className={lbl}>Monto *</label>
                    <input
                      className={inp}
                      type="number"
                      min="1"
                      step="0.01"
                      value={form.monto}
                      onChange={(e) => set("monto", e.target.value)}
                      placeholder="Ej: 150000"
                    />
                  </div>
                ) : (
                  <p className="rounded-xl bg-gray-50 px-3 py-2 text-xs text-gray-600">
                    Ejemplo: si el socio tiene $100.000 de disponible, puede pedir desde el monto mínimo
                    (parámetro MONTO MINIMO PRESTAMO) hasta $100.000. Si no tiene disponible, no ve este
                    préstamo.
                  </p>
                )}
              </Seccion>

              {/* 4. Plan y cuotas */}
              <Seccion
                numero={4}
                titulo="Plan y cuotas habilitadas"
                ayuda="El socio solo puede elegir las cuotas que marques. De esas, la app le muestra únicamente las que entran en su capacidad de pago mensual."
              >
                <div>
                  <label className={lbl}>Plan de préstamo *</label>
                  <select
                    className={inp}
                    value={form.id_plan}
                    onChange={(e) => handlePlan(e.target.value)}
                  >
                    <option value="">— Seleccionar plan —</option>
                    {planes.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                {planSeleccionado && (
                  <div className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-500">Cuotas habilitadas *</span>
                      {cuotasPlan.length > 1 && (
                        <button
                          type="button"
                          onClick={() => set("cuotas", todasMarcadas ? [] : cuotasPlan)}
                          className="text-xs text-custom-green-ccard-1 underline hover:no-underline"
                        >
                          {todasMarcadas ? "Quitar todas" : "Marcar todas"}
                        </button>
                      )}
                    </div>
                    {cuotasPlan.length === 0 ? (
                      <p className="text-xs text-red-500">
                        El plan no tiene cuotas cargadas en PRE_PLAN_PAGO_DETALLE.
                      </p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {cuotasPlan.map((c) => {
                          const marcada = form.cuotas.includes(c);
                          return (
                            <button
                              type="button"
                              key={c}
                              onClick={() => toggleCuota(c)}
                              className={`px-3 py-1 rounded-full border text-xs font-medium transition-colors ${
                                marcada
                                  ? "border-custom-green-ccard-1 bg-custom-green-ccard-1 text-white"
                                  : "border-gray-300 bg-white text-gray-600 hover:bg-gray-100"
                              }`}
                            >
                              {c} cuotas
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <p className="mt-2 text-xs text-gray-400">
                      Ejemplo: si marcás 12 y 18, y la capacidad del socio le permite de 2 a 24 cuotas,
                      solo ve 12 y 18. Si su capacidad no le alcanza para 12, solo ve 18.
                    </p>
                  </div>
                )}
              </Seccion>

              {/* 5. Visibilidad */}
              <Seccion
                numero={5}
                titulo="Convivencia con el préstamo por disponible"
                ayuda="El préstamo por disponible es el préstamo personal de siempre, donde el socio elige el monto con su disponible."
              >
                <span className={lbl}>¿Qué préstamos ve el socio? *</span>
                <div className="grid gap-2 sm:grid-cols-2">
                  <OpcionRadio
                    name="modo"
                    value={MODOS.AMBOS}
                    checked={form.modo === MODOS.AMBOS}
                    onChange={(v) => set("modo", v)}
                    titulo="Este + el préstamo por disponible"
                    detalle="Si tiene disponible, también ve el préstamo de siempre."
                  />
                  <OpcionRadio
                    name="modo"
                    value={MODOS.SOLO_PARAMETRIZADO}
                    checked={form.modo === MODOS.SOLO_PARAMETRIZADO}
                    onChange={(v) => set("modo", v)}
                    titulo="Solo este préstamo"
                    detalle="Se le oculta el préstamo por disponible."
                  />
                </div>

                <span className={`${lbl} pt-2`}>¿Requiere que tenga disponible? *</span>
                <div className="grid gap-2 sm:grid-cols-2">
                  <OpcionRadio
                    name="requiere_disponible"
                    value="S"
                    checked={montoVariable || form.requiere_disponible === "S"}
                    onChange={(v) => set("requiere_disponible", v)}
                    titulo="Sí, solo si tiene disponible"
                    detalle="Disponible mayor al MONTO MINIMO PRESTAMO."
                  />
                  <OpcionRadio
                    name="requiere_disponible"
                    value="N"
                    checked={!montoVariable && form.requiere_disponible === "N"}
                    onChange={(v) => set("requiere_disponible", v)}
                    titulo="No, mostrarlo igual"
                    detalle={
                      montoVariable
                        ? "No disponible: un préstamo según el disponible siempre lo requiere."
                        : "Se ofrece aunque no tenga disponible (con monto fijo)."
                    }
                    disabled={montoVariable}
                  />
                </div>
              </Seccion>

              {/* Resumen */}
              <div className="rounded-xl bg-blue-50 border border-blue-100 px-4 py-3 text-xs text-blue-800 space-y-1">
                <p className="font-semibold">Resumen: así funciona este préstamo en la app</p>
                <p>
                  • Lo ven:{" "}
                  {porClientes
                    ? `solo los ${clientes.length} cliente(s) de la lista.`
                    : `los socios del grupo ${nombreGrupo ?? "elegido"}${
                        form.score_min !== "" && form.score_max !== ""
                          ? ` con score entre ${form.score_min} y ${form.score_max}`
                          : ""
                      }.`}
                </p>
                <p>
                  • Monto:{" "}
                  {montoVariable
                    ? "lo elige el socio, entre el monto mínimo y su disponible."
                    : form.monto
                      ? `${formatPesos(form.monto)} fijo.`
                      : "fijo (completalo arriba)."}
                </p>
                <p>
                  • Cuotas:{" "}
                  {form.cuotas.length
                    ? `${form.cuotas.join(", ")}, solo las que entren en su capacidad de pago.`
                    : "marcá al menos una."}
                </p>
                <p>• Con disponible: {visibilidad.conDisp}.</p>
                <p>• Sin disponible: {visibilidad.sinDisp}.</p>
                <p className="pt-1 text-blue-600">
                  Si un socio recibe varios préstamos activos, todos tienen que tener la misma
                  configuración del punto 5 (mismo grupo con score superpuesto, o clientes en común).
                  Si no, la intranet no deja guardar y te muestra con cuál choca.
                </p>
              </div>

              {validationError && (
                <div className="rounded-xl bg-yellow-50 border border-yellow-200 px-4 py-3 text-sm text-yellow-800">
                  {validationError}
                </div>
              )}
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="prestamo-form"
            disabled={confirmDisabled}
            className="px-5 py-2 rounded-xl bg-custom-green-ccard-1 text-white text-sm font-medium hover:bg-custom-green-ccard-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear préstamo"}
          </button>
        </div>
      </div>
    </div>
  );
}
